/**
 * Ledger Entry Editor - Mutation Engine Hook
 *
 * Implements dirty-state and partial-success mutation engine using React Hook Form.
 *
 * Features:
 * - Original snapshot + RHF draft with dirty detection per field
 * - Capability filtering (only enabled fields are tracked)
 * - Deterministic mutation queue in exact order
 * - Sequential mutateAsync execution (each success merges before next)
 * - Loading/error state per item and overall
 * - Success merging (server values update snapshot/draft)
 * - Failure retention (failed edits remain with field errors)
 * - Deletion guard (prevents combining delete with other ops)
 * - Partial failure handling (no close, keep succeeded values)
 * - Complete success closes once
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { UseFormReturn, FieldValues, useWatch } from 'react-hook-form';
import {
  createSnapshot,
  detectDirtyFields,
  getDirtyFieldNames,
  hasAnyDirtyFields,
  buildDirtyPayload,
  mergeServerResponse,
  deepClone,
  EntrySnapshot,
  EntryDraft,
  DirtyFields,
  EditorCapabilities,
} from './dirty';
import {
  createEmptyQueue,
  addToQueue,
  validateQueue,
  setItemLoading,
  setItemResult,
  setItemError,
  hasQueueErrors,
  getQueueErrors,
  allQueueItemsSucceeded,
  getNextQueueItem,
  advanceQueue,
  isQueueComplete,
  isQueueLoading,
  getQueueErrorMessage,
  MutationQueue,
  MutationOperationType,
} from './queue';
import { normalizeLedgerError } from './errors';
import {
  createOrderLedgerEntry,
  updateOrderLedgerEntry,
  deleteOrderLedgerEntry,
  createOrderLedgerAssociation,
  updateOrderLedgerAssociation,
  deleteOrderLedgerAssociation,
  helpOrderLedgerEntryParties,
  helpOrderLinkedEntry,
  updateOrderLedgerPosted,
  deleteOrderLedgerRole,
} from '../services/mutations';

/**
 * Engine configuration.
 */
export type LedgerEditorEngineConfig = {
  capabilities?: EditorCapabilities;
  onClose?: () => void;
  onError?: (error: Error) => void;
  /**
   * Optional reconciliation callback for server response merging.
   * Called after successful mutations to merge server values into
   * React Query cache and invalidate order-ledger queries.
   */
  onReconcile?: (response: Record<string, unknown>) => void;
};

/**
 * Engine state and API.
 */
export type LedgerEditorEngine = {
  // Form integration
  formMethods: UseFormReturn<FieldValues>;

  // State
  snapshot: EntrySnapshot;
  draft: EntryDraft;
  dirtyFields: DirtyFields;

  // Submission state
  isSubmitting: boolean;
  errors: Record<string, string>;
  fieldErrors: Record<string, string>;

  // Queue state
  queue: MutationQueue;
  queueError: string | null;

  // Actions
  submit: () => Promise<void>;
  deleteEntry: () => Promise<void>;
  canSubmit: boolean;
  reset: () => void;
};

/**
 * Create the ledger entry editor engine.
 * Wraps React Hook Form with snapshot/draft, dirty detection, and sequential mutation queue.
 */
export function useLedgerEditorEngine(
  formMethods: UseFormReturn<FieldValues>,
  initialEntry: EntryDraft,
  config: LedgerEditorEngineConfig = {},
): LedgerEditorEngine {
  // Snapshot and draft state
  const [snapshot, setSnapshot] = useState<EntrySnapshot>(() => createSnapshot(initialEntry));
  const [draft, setDraft] = useState<EntryDraft>(() => deepClone(initialEntry));
  const [dirtyFields, setDirtyFields] = useState<DirtyFields>({});

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Queue state
  const [queue, setQueue] = useState<MutationQueue>(createEmptyQueue());
  const [queueError, setQueueError] = useState<string | null>(null);

  // Refs for mutation execution
  const queueRef = useRef<MutationQueue>(queue);
  const draftRef = useRef<EntryDraft>(draft);
  const snapshotRef = useRef<EntrySnapshot>(snapshot);

  // Keep refs in sync
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  // Subscribe to form value changes and sync into draft state.
  // useWatch re-renders this hook whenever any registered field changes,
  // which triggers the dirty detection effect below.
  const watchedValues = useWatch({ control: formMethods.control });

  useEffect(() => {
    setDraft(prev => {
      const next = { ...prev, ...watchedValues };
      if (JSON.stringify(prev) === JSON.stringify(next)) {
        return prev;
      }
      return next;
    });
  }, [watchedValues]);

  // Update dirty fields when draft changes
  useEffect(() => {
    const newDirtyFields = detectDirtyFields(snapshot, draft, config.capabilities);
    setDirtyFields(prev => {
      if (JSON.stringify(prev) === JSON.stringify(newDirtyFields)) {
        return prev;
      }
      return newDirtyFields;
    });
  }, [draft, snapshot, config.capabilities]);

  /**
   * Execute a single mutation from the queue.
   */
  const executeMutation = useCallback(
    async (type: MutationOperationType, payload: unknown): Promise<unknown> => {
      switch (type) {
        case 'createEntry':
          return createOrderLedgerEntry(payload as any);
        case 'updateEntry':
          return updateOrderLedgerEntry(payload as any);
        case 'deleteEntry':
          return deleteOrderLedgerEntry(payload as any);
        case 'createAssociation':
          return createOrderLedgerAssociation(payload as any);
        case 'updateAssociation':
          return updateOrderLedgerAssociation(payload as any);
        case 'deleteAssociation':
          return deleteOrderLedgerAssociation(payload as any);
        case 'helpParties':
          return helpOrderLedgerEntryParties(payload as any);
        case 'helpLinkedEntry':
          return helpOrderLinkedEntry(payload as any);
        case 'updatePosted':
          return updateOrderLedgerPosted(payload as any);
        case 'deleteRole':
          return deleteOrderLedgerRole(payload as any);
        default:
          throw new Error(`Unknown mutation type: ${type}`);
      }
    },
    [],
  );

  /**
   * Process the mutation queue sequentially.
   * Each success merges before the next payload is built.
   */
  const processQueue = useCallback(async () => {
    let currentQueue = queueRef.current;

    while (!isQueueComplete(currentQueue)) {
      const item = getNextQueueItem(currentQueue);
      if (!item) break;

      const itemIndex = currentQueue.currentIndex;

      try {
        // Mark as loading
        currentQueue = setItemLoading(currentQueue, itemIndex, true);
        setQueue(currentQueue);

        // Execute mutation
        const result = await executeMutation(item.type, item.payload);

        // Merge server response into snapshot and draft
        if (result && typeof result === 'object') {
          const { snapshot: newSnapshot, draft: newDraft } = mergeServerResponse(
            snapshotRef.current,
            draftRef.current,
            result as Record<string, unknown>,
          );
          snapshotRef.current = newSnapshot;
          draftRef.current = newDraft;
          setSnapshot(newSnapshot);
          setDraft(newDraft);

          // Reconcile with React Query cache and invalidate order-ledger queries
          config.onReconcile?.(result as Record<string, unknown>);
        }

        // Mark as succeeded
        currentQueue = setItemResult(currentQueue, itemIndex, result);
        setQueue(currentQueue);
      } catch (error) {
        // Mark as failed, retain draft values
        const normalizedMsg = normalizeLedgerError(error);
        const err = new Error(normalizedMsg);
        currentQueue = setItemError(currentQueue, itemIndex, err);
        setQueue(currentQueue);

        // Set field error for visibility
        setFieldErrors(prev => ({
          ...prev,
          [item.type]: normalizedMsg,
        }));

        // Stop processing on first failure (partial success)
        break;
      }

      // Advance to next item
      currentQueue = advanceQueue(currentQueue);
    }

    // Check final state
    const hasErrors = hasQueueErrors(currentQueue);
    const allSucceeded = allQueueItemsSucceeded(currentQueue);

    // Always settle engine state before invoking callbacks that may unmount component
    setIsSubmitting(false);

    if (allSucceeded && !hasErrors) {
      // Complete success: close (after state is settled)
      config.onClose?.();
    } else if (hasErrors) {
      // Partial failure: keep error visible, don't close
      const errorMsg = getQueueErrorMessage(currentQueue);
      setQueueError(errorMsg);
      if (config.onError) {
        const firstError = Array.from(getQueueErrors(currentQueue).values())[0];
        config.onError(firstError);
      }
    }
  }, [executeMutation, config]);

  /**
   * Submit the form.
   * Validates queue, builds mutations, executes sequentially.
   */
  const submit = useCallback(async () => {
    setIsSubmitting(true);
    setErrors({});
    setFieldErrors({});
    setQueueError(null);

    try {
      // Get current form values
      const formValues = formMethods.getValues();

      // Update draft from form
      setDraft(formValues);
      draftRef.current = formValues;

      // Detect dirty fields
      const newDirtyFields = detectDirtyFields(snapshot, formValues, config.capabilities);
      setDirtyFields(newDirtyFields);

      // Check if anything is dirty
      if (!hasAnyDirtyFields(newDirtyFields)) {
        setIsSubmitting(false);
        return;
      }

      let newQueue = createEmptyQueue();

      const dirtyPayload = buildDirtyPayload(formValues, newDirtyFields);

      if (newDirtyFields.entryId) {
        newQueue = addToQueue(newQueue, 'createEntry', dirtyPayload);
      }

      const needsUpdate =
        newDirtyFields.amount ||
        newDirtyFields.code ||
        newDirtyFields.description ||
        newDirtyFields.kind ||
        newDirtyFields.entryDate ||
        newDirtyFields.letter ||
        newDirtyFields.number ||
        newDirtyFields.months ||
        newDirtyFields.months_advanced ||
        newDirtyFields.annuality;

      if (needsUpdate) {
        const updatePayload = {
          _id: formValues.orderId,
          accounting_id: formValues.entryId,
          data: {
            ...(newDirtyFields.amount && { amount_override: formValues.amount }),
            ...(newDirtyFields.code && { code: formValues.code }),
            ...(newDirtyFields.description && { description: formValues.description }),
            ...(newDirtyFields.entryDate && { entry_date: formValues.entryDate }),
            ...(newDirtyFields.letter && { letter: formValues.letter }),
            ...(newDirtyFields.number && { number: formValues.number }),
            ...(newDirtyFields.months && { months: formValues.months }),
            ...(newDirtyFields.months_advanced && { months_advanced: formValues.months_advanced }),
            ...(newDirtyFields.annuality && { annuality: formValues.annuality }),
          },
        };
        newQueue = addToQueue(newQueue, 'updateEntry', updatePayload);
      }

      if (newDirtyFields.posted) {
        const postedPayload = {
          _id: formValues.orderId,
          accounting_id: formValues.entryId,
          posted: formValues.posted,
        };
        newQueue = addToQueue(newQueue, 'updatePosted', postedPayload);
      }

      // Validate queue
      const validation = validateQueue(newQueue);
      if (!validation.valid) {
        setErrors({ queue: validation.error || 'Invalid queue' });
        setIsSubmitting(false);
        return;
      }

      // Execute queue
      setQueue(newQueue);
      queueRef.current = newQueue;
      await processQueue();
    } catch (error) {
      const normalizedMsg = normalizeLedgerError(error);
      setErrors({ submit: normalizedMsg });
      setIsSubmitting(false);
      const err = new Error(normalizedMsg);
      config.onError?.(err);
    }
  }, [formMethods, snapshot, config, processQueue]);

  /**
   * Delete the entry.
   * Queues a deleteEntry mutation and processes it.
   * Payload: _id (order ID) and accounting_id (accounting entry ID).
   */
  const deleteEntry = useCallback(async () => {
    setIsSubmitting(true);
    setErrors({});
    setFieldErrors({});
    setQueueError(null);

    try {
      const payload = {
        _id: draftRef.current.orderId,
        accounting_id: draftRef.current.entryId,
      };

      let newQueue = createEmptyQueue();
      newQueue = addToQueue(newQueue, 'deleteEntry', payload);

      const validation = validateQueue(newQueue);
      if (!validation.valid) {
        setErrors({ queue: validation.error || 'Invalid queue' });
        setIsSubmitting(false);
        return;
      }

      setQueue(newQueue);
      queueRef.current = newQueue;
      await processQueue();
    } catch (error) {
      const normalizedMsg = normalizeLedgerError(error);
      setErrors({ delete: normalizedMsg });
      setIsSubmitting(false);
      const err = new Error(normalizedMsg);
      config.onError?.(err);
    }
  }, [processQueue, config]);

  /**
   * Reset to initial state.
   */
  const reset = useCallback(() => {
    const newSnapshot = createSnapshot(initialEntry);
    const newDraft = deepClone(initialEntry);

    setSnapshot(newSnapshot);
    setDraft(newDraft);
    setDirtyFields({});
    setIsSubmitting(false);
    setErrors({});
    setFieldErrors({});
    setQueue(createEmptyQueue());
    setQueueError(null);

    formMethods.reset(newDraft);
  }, [formMethods, initialEntry]);

  return {
    formMethods,
    snapshot,
    draft,
    dirtyFields,
    isSubmitting,
    errors,
    fieldErrors,
    queue,
    queueError,
    submit,
    deleteEntry,
    canSubmit: hasAnyDirtyFields(dirtyFields) && !isSubmitting,
    reset,
  };
}
